import { Badge } from "@/components/ui/badge";
import { ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Level6Item } from "./Level6Item";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Level5ItemProps = {
  item: {
    id: number;
    name: string;
    index: string;
    title?: string;
    description?: string;
    node_1?: string;
    node_2?: string;
    node_3?: string;
    node_4?: string;
    node_5?: string;
    product_count?: string;
    url?: string;
    cat_text?: string;
  };
  isExpanded: boolean;
  onToggle: () => void;
};

export const Level5Item = ({ item, isExpanded, onToggle }: Level5ItemProps) => {
  const { data: level6Categories } = useQuery({
    queryKey: ["level6-categories", item.name, isExpanded],
    enabled: isExpanded,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories_atp_autoteile_de")
        .select("*")
        .eq("level", 6)
        .eq("parent_node_name", item.name);

      if (error) throw error;
      return data.sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
          <ChevronRight
            className={`h-5 w-5 transition-transform ${
              isExpanded ? "rotate-90" : ""
            }`}
          />
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="font-medium">
                  {item.url ? (
                    <a 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {item.name}
                    </a>
                  ) : (
                    item.name
                  )}
                </div>
                <Badge 
                  variant={item.index === "index, follow" ? "default" : "destructive"}
                >
                  {item.index}
                </Badge>
                <Badge
                  variant={item.cat_text ? "default" : "secondary"}
                  className={item.cat_text ? "bg-green-500" : "bg-red-200"}
                >
                  {item.cat_text ? "Found content" : "no content"}
                </Badge>
              </div>
              {item.product_count && (
                <div className="text-sm text-muted-foreground">
                  Products: {item.product_count}
                </div>
              )}
            </div>
            {item.title && (
              <p className="text-sm text-muted-foreground text-left font-medium">
                {item.title}
              </p>
            )}
            {item.description && (
              <p className="text-sm text-muted-foreground text-left">
                {item.description}
              </p>
            )}
            <div className="text-sm text-muted-foreground">
              {(item.node_1 || item.node_2 || item.node_3 || item.node_4 || item.node_5) && (
                <Breadcrumb>
                  <BreadcrumbList>
                    {item.node_1 && (
                      <>
                        <BreadcrumbItem>
                          <BreadcrumbLink href="#">{item.node_1}</BreadcrumbLink>
                        </BreadcrumbItem>
                        {(item.node_2 || item.node_3 || item.node_4 || item.node_5) && <BreadcrumbSeparator />}
                      </>
                    )}
                    {item.node_2 && (
                      <>
                        <BreadcrumbItem>
                          <BreadcrumbLink href="#">{item.node_2}</BreadcrumbLink>
                        </BreadcrumbItem>
                        {(item.node_3 || item.node_4 || item.node_5) && <BreadcrumbSeparator />}
                      </>
                    )}
                    {item.node_3 && (
                      <>
                        <BreadcrumbItem>
                          <BreadcrumbLink href="#">{item.node_3}</BreadcrumbLink>
                        </BreadcrumbItem>
                        {(item.node_4 || item.node_5) && <BreadcrumbSeparator />}
                      </>
                    )}
                    {item.node_4 && (
                      <>
                        <BreadcrumbItem>
                          <BreadcrumbLink href="#">{item.node_4}</BreadcrumbLink>
                        </BreadcrumbItem>
                        {item.node_5 && <BreadcrumbSeparator />}
                      </>
                    )}
                    {item.node_5 && (
                      <BreadcrumbItem>
                        <BreadcrumbLink href="#">{item.node_5}</BreadcrumbLink>
                      </BreadcrumbItem>
                    )}
                  </BreadcrumbList>
                </Breadcrumb>
              )}
            </div>
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-8 space-y-2 mt-2">
          {level6Categories?.map((level6Item) => (
            <Level6Item
              key={level6Item.id}
              item={level6Item}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};